/**
 * Settings → Shared report links (QA 2026-10-16). OutlierOS had no place to turn a shared report off; ViralWise lists
 * the user's live `/r/…` links (GET /api/shares) with Copy link and Turn off (DELETE /api/shares/:id, confirmed).
 */
import { useEffect, useState } from 'react';
import { Copy } from 'lucide-react';
import { parseWindow, windowLabel, DEFAULT_WINDOW } from '@vw/core/src/report';
import { plural } from '@vw/core/src/plural';
import { Button, ConfirmDialog, Spinner, copyText, toast } from '@vw/ui';
import { api } from '../api';
import { date } from '../format';
import { Link } from '../router';

export interface ShareLink { id: string; url: string; handle: string | null; available: boolean; window: string | null; views: number; createdAt: number }

/** "All reels" / "Latest 200 reels" for a stored window key (links made before windows existed use the default: all reels since decision 84). */
export const shareWindowLabel = (key: string | null) => windowLabel(parseWindow(key) ?? DEFAULT_WINDOW);

export function SharedLinks() {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [off, setOff] = useState<ShareLink | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => api<{ shares: ShareLink[] }>('/api/shares').then((r) => setLinks(r.shares), () => setLinks([]));
  useEffect(() => { void load(); }, []);

  async function turnOff(l: ShareLink) {
    setBusy(true);
    try {
      await api(`/api/shares/${l.id}`, { method: 'DELETE' });
      setLinks((x) => (x ?? []).filter((y) => y.id !== l.id));
      toast.success('Link turned off', { description: 'Anyone opening it now sees that it was turned off.' });
    } catch (e) {
      toast.error('Could not turn the link off', { description: (e as Error).message });
    } finally {
      setBusy(false);
      setOff(null);
    }
  }

  if (!links) return <Spinner />;
  if (!links.length) {
    return <p className="rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground">No shared report links. Use <b>Share report</b> on a creator's page to make one.</p>;
  }
  return (
    <>
      <ul className="divide-y divide-border rounded-2xl border border-border">
        {links.map((l) => (
          <li key={l.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
            <span className="hidden text-xl sm:block" aria-hidden="true">🔗</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold">
                {l.handle ? <Link to={`/c/${l.handle}`} className="hover:text-primary">@{l.handle}</Link> : 'Creator no longer available'}
                <span className="font-normal text-muted-foreground"> · {shareWindowLabel(l.window)}</span>
              </div>
              <div className="text-xs text-muted-foreground">Created {date(l.createdAt)} · {plural(l.views, 'view')}{!l.available ? ' · the report is hidden, so the link shows nothing' : ''}</div>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant="secondary" onClick={() => void copyText(l.url, { success: 'Link copied', description: l.url })}><Copy className="h-3.5 w-3.5" />Copy link</Button>
              <Button size="sm" variant="outline-danger" onClick={() => setOff(l)}>Turn off</Button>
            </div>
          </li>
        ))}
      </ul>
      <ConfirmDialog open={!!off} danger confirmLabel="Turn off" busy={busy} title={`Turn off the link to ${off?.handle ? `@${off.handle}'s` : 'this'} report?`}
        body={<p>Anyone who opens it will see that the link was turned off. This can't be undone, but you can share the report again to make a new link.</p>}
        onCancel={() => setOff(null)} onConfirm={() => off && turnOff(off)} />
    </>
  );
}
